"use client";

import Image from "next/image";
import Link from "next/link";
import { Autoplay, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { getPageRoute, getS3ImageUrl } from "@/utils/routes";

import "swiper/css";
import "swiper/css/pagination";

export default function HeroSectionNewVersion({ widget }: any) {
  const data = widget?.data || {};
  const images = data?.images || [];
  const rightTopImage = data?.rightTopImage;
  const rightBottomImage = data?.rightBottomImage;
  const rightTopLink = data?.rightTopLink || {};
  const rightBottomLink = data?.rightBottomLink || {};
  const settings = data?.settings || {};

  if (!images?.length && !rightTopImage && !rightBottomImage) return null;

  const width = settings?.width || 1920;
  const height = settings?.height || 710;
  const autoplay = settings?.autoplay
    ? {
        delay: settings?.delay || 5000,
        disableOnInteraction: false,
      }
    : false;

  return (
    <div className="container mx-auto px-2 md:px-0 py-2">
      <div className="flex flex-col md:flex-row gap-2 md:gap-2.5 items-stretch">
        {/* Left Side — Main Slider */}
        <div className="w-full md:w-[74%]">
          <Swiper
            spaceBetween={0}
            slidesPerView={1}
            loop={images.length > 1}
            pagination={{ clickable: true }}
            autoplay={autoplay}
            modules={[Autoplay, Pagination]}
            className="h-full rounded-lg md:rounded-xl overflow-hidden [&_.swiper-pagination]:bottom-3 [&_.swiper-pagination-bullet]:bg-white/60 [&_.swiper-pagination-bullet]:w-2.5 [&_.swiper-pagination-bullet]:h-2.5 [&_.swiper-pagination-bullet-active]:bg-primary [&_.swiper-pagination-bullet-active]:w-7 [&_.swiper-pagination-bullet-active]:rounded-full"
          >
            {images?.map((item: any, index: number) => {
              const rawHref = item?.link?.url || item?.url || "#";
              const href = rawHref.startsWith("http")
                ? rawHref
                : getPageRoute(item?.link?.urlType || "custom", rawHref);

              return (
                <SwiperSlide key={index} className="h-full">
                  <Link href={href} className="block h-full" passHref>
                    <Image
                      src={getS3ImageUrl(item.image)}
                      alt={item?.title || `slide-${index + 1}`}
                      width={width}
                      height={height}
                      className="w-full h-full"
                      style={{ aspectRatio: `${width} / ${height}` }}
                      unoptimized={true}
                    />
                  </Link>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>

        {/* Right Side */}
        <div className="w-full md:w-[26%] flex flex-row lg:flex-col gap-2 md:gap-2.5 h-full">
          {/* Top Banner */}
          {rightTopImage && (
            <div className="flex-1 min-h-0">
              <Link
                href={rightTopLink?.url || "#"}
                passHref
                className="block h-full"
              >
                <Image
                  src={getS3ImageUrl(rightTopImage)}
                  alt="right top banner"
                  width={315}
                  height={252}
                  className="w-full h-full rounded-lg md:rounded-xl"
                  unoptimized={true}
                />
              </Link>
            </div>
          )}

          {/* Bottom Banner */}
          {rightBottomImage && (
            <div className="flex-1 min-h-0">
              <Link
                href={rightBottomLink?.url || "#"}
                passHref
                className="block h-full"
              >
                <Image
                  src={getS3ImageUrl(rightBottomImage)}
                  alt="right bottom banner"
                  width={315}
                  height={252}
                  className="w-full h-full rounded-lg md:rounded-xl"
                  unoptimized={true}
                />
              </Link>
            </div>
          )}

          {/* No banners */}
          {!rightTopImage && !rightBottomImage && (
            <div className="hidden md:flex flex-1 items-center justify-center bg-gray-100 rounded-xl text-gray-400 text-sm">
              No banners
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
